import React, { useState } from 'react';
import { Button, Card, Col, Form, Alert } from 'react-bootstrap';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import ParticlesAuth from './ParticlesAuth';
import api from '../../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/reset-password', {
        token,
        email,
        password: formData.password,
        password_confirmation: formData.password_confirmation
      });

      if (response.data && response.data.status === 'success') {
        setSuccess(response.data.message || 'Password has been reset successfully. You can now login with your new password.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData) {
        // Handle validation errors
        if (errorData.errors) {
          // Check for password validation errors
          if (errorData.errors.password) {
            setError(errorData.errors.password[0] || 'Password validation failed.');
          } else if (errorData.errors.password_confirmation) {
            setError(errorData.errors.password_confirmation[0] || 'Password confirmation does not match.');
          } else {
            // Get first error message
            const firstErrorKey = Object.keys(errorData.errors)[0];
            setError(errorData.errors[firstErrorKey][0] || 'Validation failed.');
          }
        } else if (errorData.message) {
          setError(errorData.message);
        } else {
          setError('Failed to reset password. The link may have expired. Please request a new one.');
        }
      } else {
        setError('Failed to reset password. The link may have expired. Please request a new one.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <ParticlesAuth>
        <Col xxl={6} className="mx-auto">
          <Card className="mb-0 border-0 shadow-none mb-0">
            <Card.Body className="p-sm-5 m-lg-4">
              <Alert variant="danger">
                Invalid reset link. Please request a new password reset.
              </Alert>
              <div className="text-center mt-4">
                <Link to="/forgot-password" className="btn btn-primary">
                  Request New Reset Link
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </ParticlesAuth>
    );
  }

  return (
    <ParticlesAuth>
      <Col xxl={6} className="mx-auto">
        <Card className="mb-0 border-0 shadow-none mb-0">
          <Card.Body className="p-sm-5 m-lg-4">
            <div className="text-center">
              <h5 className="fs-3xl">Create New Password</h5>
              <p className="text-muted mb-5">
                Your new password must be different from the previous used password.
              </p>
            </div>

            <div className="p-2">
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <div className="position-relative auth-pass-inputgroup">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      className="form-control bg-light border-light pe-5 password-input"
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      minLength="8"
                    />
                    <button
                      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={showPassword ? 'ri-eye-off-fill align-middle' : 'ri-eye-fill align-middle'}></i>
                    </button>
                  </div>
                  <Form.Text>Your password must be 8-20 characters long.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <div className="position-relative auth-pass-inputgroup mb-3">
                    <Form.Control
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="form-control bg-light border-light pe-5 password-input"
                      placeholder="Confirm password"
                      value={formData.password_confirmation}
                      onChange={(e) => setFormData({...formData, password_confirmation: e.target.value})}
                      required
                      minLength="8"
                    />
                    <button
                      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={showConfirmPassword ? 'ri-eye-off-fill align-middle' : 'ri-eye-fill align-middle'}></i>
                    </button>
                  </div>
                </Form.Group>

                <div className="mt-4">
                  <Button 
                    className="btn btn-primary w-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </Form>
            </div>

            <div className="mt-4 text-center">
              <p className="mb-0">
                Wait, I remember my password...{' '}
                <Link to="/login" className="fw-semibold text-primary text-decoration-underline">
                  Click here
                </Link>
              </p>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </ParticlesAuth>
  );
};

export default ResetPassword;

